/**
 * Markdown leve sanitizado pra corpo de comentários (FEAT-V2-002 PR 2).
 *
 * Suporta:
 * - **negrito**, *itálico*
 * - `código inline`
 * - - lista
 * - 1. lista numerada
 * - link auto-detectado (http/https)
 * - quebras de linha (newlines viram <br>)
 *
 * Sanitização via DOMPurify (XSS-safe):
 * - Permite só tags básicas (a, p, strong, em, code, ul, ol, li, br, blockquote)
 * - Bloqueia <script>, atributos perigosos, javascript: URLs
 *
 * Retorna string HTML pronta pra `dangerouslySetInnerHTML`.
 */

import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Config marked: enable breaks (newline → <br>), gfm features mínimas
marked.setOptions({
  breaks: true,
  gfm: true,
});

// BUG-B1.6.2 (2026-05-28): adiciona <img> + class pra renderizar fotos
// inline em comentários do card Corretiva (CommentBody substitui R2 keys
// por <img>). class/loading aceitos pra controle visual.
const ALLOWED_TAGS = ['a', 'p', 'strong', 'em', 'code', 'ul', 'ol', 'li', 'br', 'blockquote', 'img'];
const ALLOWED_ATTR = ['href', 'target', 'rel', 'src', 'alt', 'class', 'loading'];

/**
 * Renderiza markdown como HTML sanitizado. Falha silenciosa: input ruim
 * volta como texto bruto escapado (em vez de explodir UI).
 */
export function renderMarkdown(input: string): string {
  if (!input) return '';
  try {
    const rawHtml = marked.parse(input, { async: false }) as string;
    const clean = DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      // Força target=_blank rel=noopener em links externos
      ADD_ATTR: ['target', 'rel'],
    });
    // Pós-processamento: links auto-detectados ganham target=_blank
    return clean.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
  } catch (err) {
    console.warn('[render-markdown] parse falhou, retornando texto bruto:', err);
    // Fallback: escapa < > & e troca \n por <br>
    return escapeHtml(input).replace(/\n/g, '<br>');
  }
}

/**
 * FASE-C — variante pra ARTIGO de ajuda: mesmo saneamento, allowlist maior.
 *
 * O artigo é texto longo escrito por admin: precisa de título de seção,
 * tabela e linha divisória, que a allowlist de comentário não tem (o Portal
 * de Ajuda mostrava o markdown cru dentro de um `<pre>`).
 */
const ALLOWED_TAGS_ARTIGO = [
  ...ALLOWED_TAGS,
  'h1',
  'h2',
  'h3',
  'h4',
  'hr',
  'pre',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

export function renderMarkdownArtigo(input: string): string {
  if (!input) return '';
  try {
    const rawHtml = marked.parse(input, { async: false }) as string;
    const clean = DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: ALLOWED_TAGS_ARTIGO,
      ALLOWED_ATTR,
      ADD_ATTR: ['target', 'rel'],
    });
    return clean.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
  } catch (err) {
    console.warn('[render-markdown] artigo: parse falhou, texto bruto:', err);
    return escapeHtml(input).replace(/\n/g, '<br>');
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
